import { memo, useCallback, useEffect, useState } from "react";
import { DateTime } from "luxon";
import { useLocalization } from "@fluent/react";
import { catcat as cc } from "cerulean";
import { modules, entities } from "byzantine";
import {
  MAX_MEMO_LENGTH,
  getReadableTimeFromCutoff,
} from "byzantine/src/dbbl/businessLogic/modules/achPayments";
import {
  Alert,
  Button,
  DateInput,
  ResponsiveFlex,
  Row,
  TextInput,
  Tooltip,
} from "@narmi/design_system";
import * as formAdapters from "../../../src/adapters";
import type { InvoiceFieldHandlers } from "./useInvoiceHighlight";
import { useInvoiceFlow } from "./InvoiceFlowContext";

import { useInstitutionSettings } from "../../contexts/InstitutionSettingsContext";
import LimitsAndDisclosuresDialog from "../ach/LimitsAndDisclosuresDialog";
import paymentStyles from "../ach/PaymentScreen.module.scss";
import styles from "./InvoiceContainer.module.scss";

type InvoiceStartDateFieldProps = {
  label: string;
  minDate: string;
  value: string;
  error?: string;
  extractionFailed: boolean;
  disabled: boolean;
  id: string;
  onChange: (value: string) => void;
  onBlur: (event: unknown) => void;
  handlers: InvoiceFieldHandlers;
};

/**
 * `DateInput` rebuilds its flatpickr instance on every render (its effect
 * depends on a config object rebuilt inline) and initializing destroys any open
 * calendar. Focusing the field updates the preview highlight, which re-renders
 * this section — so without memoization the calendar is torn down the instant
 * it opens. Every prop here must stay referentially stable for that to hold.
 */
const InvoiceStartDateField = memo(
  ({
    label,
    minDate,
    value,
    error,
    extractionFailed,
    disabled,
    id,
    onChange,
    onBlur,
    handlers,
  }: InvoiceStartDateFieldProps) => (
    <div
      className={extractionFailed ? styles.extractionFailedField : undefined}
      onFocus={handlers.onFocus}
      onBlur={handlers.onBlur}
    >
      <DateInput
        minDate={minDate}
        dateFormat={"m/d/Y"}
        altInput={true}
        altFormat={"m/d/Y"}
        field="date"
        label={label}
        useIsoOnChange={false}
        onChange={onChange}
        id={id}
        value={value}
        defaultDate={disabled ? value : undefined}
        error={error}
        disabled={disabled}
        onBlur={onBlur}
      />
    </div>
  )
);
InvoiceStartDateField.displayName = "InvoiceStartDateField";

type InvoicePaymentDetailsSectionProps = {
  /** Stable per-field focus handlers that drive the invoice preview highlight. */
  fieldHandlers: Record<"amount" | "date" | "memo", InvoiceFieldHandlers>;
};

const InvoicePaymentDetailsSection = ({
  fieldHandlers,
}: InvoicePaymentDetailsSectionProps) => {
  const { l10n } = useLocalization();
  const institution = useInstitutionSettings();
  const limits = entities.limits.useLimits();
  const form = modules.achPayments.ACHPaymentForm.useForm();
  const { requiresManualEntry, invoice } = useInvoiceFlow();
  const isInstantDelivery = modules.achPayments.useIsInstantACHDelivery();
  const isExpeditedDelivery = modules.achPayments.useIsExpeditedACHDelivery();
  const arrivalEstimateLabel = modules.achPayments.useACHArrivalEstimateLabel();
  const [showLimitsDialog, setShowLimitsDialog] = useState(false);

  const { setFieldTouched, setFieldValue, touched, values } = form;
  const minDate = modules.achPayments.getStartDate(
    institution.ach_next_cutoff,
    institution.time_zone
  );
  const expeditedStartDate = modules.achPayments.getInstitutionToday(
    institution.time_zone
  );
  const cutoffTime = getReadableTimeFromCutoff(institution.ach_next_cutoff);

  // holidays are only needed to compute the standard ACH arrival estimate;
  // fetch them in the background and let the estimate self-correct once loaded
  const { send: fetchHolidays, hasData: hasHolidays } =
    entities.holidays.useFetchHolidays();
  useEffect(() => {
    if (!isExpeditedDelivery && !hasHolidays) {
      fetchHolidays();
    }
  }, [isExpeditedDelivery, fetchHolidays, hasHolidays]);

  // Expedited speeds (instant, same day) use today; standard delivery keeps the
  // date at or after the earliest valid date.
  useEffect(() => {
    if (isExpeditedDelivery) {
      if (values.startDate !== expeditedStartDate) {
        setFieldValue("startDate", expeditedStartDate);
      }
      return;
    }
    const startDate = DateTime.fromFormat(values.startDate, "MM/dd/yyyy");
    const earliest = DateTime.fromFormat(minDate, "MM/dd/yyyy");
    if (!startDate.isValid || startDate < earliest) {
      setFieldValue("startDate", minDate);
    }
  }, [
    expeditedStartDate,
    isExpeditedDelivery,
    minDate,
    values.startDate,
    setFieldValue,
  ]);

  const {
    id: startDateId,
    value: startDateValue,
    error: startDateError,
    onBlur: startDateOnBlur,
  } = formAdapters.fieldWithOnChange(form, "startDate");

  // `setFieldValue` is stable, so the memoized date field keeps the same
  // `onChange` reference across renders
  const handleStartDateChange = useCallback(
    (value: string) => {
      setFieldTouched("startDate", true, false);
      setFieldValue(
        "startDate",
        DateTime.fromFormat(value, "M/d/yyyy").toFormat("MM/dd/yyyy"),
        true
      );
    },
    [setFieldTouched, setFieldValue]
  );

  const dateLabel = l10n.getString("label-date-once");
  const internalNoteTooltipText = l10n.getString(
    "invoice-payment-internal-note-tooltip"
  );
  const internalNoteTooltip = (
    <Tooltip text={internalNoteTooltipText}>
      <span
        className="narmi-icon-info"
        role="img"
        aria-label={internalNoteTooltipText}
        tabIndex={0}
        style={{ fontSize: "20px" }}
      />
    </Tooltip>
  );

  const isForeignCurrency = entities.invoices.isInvoiceForeignCurrency(
    invoice?.extracted
  );
  const missingFields = entities.invoices.getInvoiceMissingFields(
    invoice?.extracted
  );
  const amountExtractionFailed =
    requiresManualEntry && missingFields.amount && !values.amount;
  const dateExtractionFailed =
    requiresManualEntry && missingFields.date && !touched.startDate;

  const toggleLimitsDialog = () => {
    setShowLimitsDialog(!showLimitsDialog);
  };

  return (
    <>
      <ResponsiveFlex gapSize="m">
        <div className="alignChild--left--center">
          <h4 className="fontSize--m nds-sans">
            {l10n.getString("ach-payment-payment-details-title")}
          </h4>
          {!isInstantDelivery ? (
            <div className={paymentStyles.endRowContainer}>
              <Button
                label={l10n.getString("ach-payment-view-limits-bulk-cta")}
                kind="plain"
                size="xs"
                testId="invoice-view-limits"
                onClick={toggleLimitsDialog}
              />
            </div>
          ) : null}
        </div>

        {requiresManualEntry ? (
          <Alert isActive isDismissable={false} kind="warn" icon="info">
            <span>
              {l10n.getString("invoice-details-extraction-failed-warning")}
            </span>
          </Alert>
        ) : null}

        {isForeignCurrency ? (
          <Alert isActive isDismissable={false} kind="warn" icon="info">
            <span>
              {l10n.getString("invoice-details-foreign-currency-warning")}
            </span>
          </Alert>
        ) : null}

        {/* React's onFocus/onBlur bubble, so wrapping catches focus from the
            inner input without each control having to forward handlers */}
        <div
          className={
            amountExtractionFailed ? styles.extractionFailedField : undefined
          }
          onFocus={fieldHandlers.amount.onFocus}
          onBlur={fieldHandlers.amount.onBlur}
        >
          <TextInput
            label={l10n.getString("label-amount")}
            {...formAdapters.amountInput(form, "amount")}
          />
        </div>

        <div>
          {!isExpeditedDelivery && cutoffTime ? (
            <Row alignItems="center" justifyContent="end">
              <Row.Item shrink>
                <span
                  className={cc([
                    "fontColor--secondary fontSize--s",
                    paymentStyles.dateInputCutoffLabel,
                  ])}
                >
                  {l10n.getString("ach-payment-cutoff-time", { cutoffTime })}
                </span>
              </Row.Item>
            </Row>
          ) : null}
          <InvoiceStartDateField
            label={dateLabel}
            minDate={isExpeditedDelivery ? expeditedStartDate : minDate}
            id={startDateId}
            value={isExpeditedDelivery ? expeditedStartDate : startDateValue}
            error={isExpeditedDelivery ? undefined : startDateError}
            extractionFailed={
              isExpeditedDelivery ? false : dateExtractionFailed
            }
            disabled={isExpeditedDelivery}
            onBlur={startDateOnBlur}
            onChange={handleStartDateChange}
            handlers={fieldHandlers.date}
          />
          {!isExpeditedDelivery ? (
            <div
              className={cc([
                "fontColor--secondary fontSize--s margin--top--xs",
              ])}
            >
              {arrivalEstimateLabel}
            </div>
          ) : null}
        </div>

        <div
          onFocus={fieldHandlers.memo.onFocus}
          onBlur={fieldHandlers.memo.onBlur}
        >
          <TextInput
            label={l10n.getString("label-memo")}
            maxLength={MAX_MEMO_LENGTH}
            {...formAdapters.input(form, "description")}
          />
        </div>

        <TextInput
          label={l10n.getString("label-internal-note-optional")}
          maxLength={MAX_MEMO_LENGTH}
          endContent={internalNoteTooltip}
          {...formAdapters.input(form, "memo")}
        />
      </ResponsiveFlex>
      <LimitsAndDisclosuresDialog
        isOpen={showLimitsDialog}
        handleClose={toggleLimitsDialog}
        limits={limits}
      />
    </>
  );
};

export default InvoicePaymentDetailsSection;
